import {FormattedMessage} from 'react-intl';
import useSupercluster from 'use-supercluster';
import GoogleMap from 'google-maps-react-markers';
import React, {useEffect, useRef, useState} from 'react';

import {SearchTypes} from 'features/search/enums';
import {SearchResult} from "features/search/types";
import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import { useSearchData } from 'features/search/services/SearchService/query';
import {ResidenceType, StageStatus} from 'features/search/filters/enums';

import './style.scss';

import {Marker} from 'components/organisms/Map/_marker';
import {Cluster} from 'components/organisms/Map/_cluster';
import {MarkerDot} from 'components/organisms/Map/_markerDot';
import {CardHolder} from 'components/organisms/Map/_cardHolder';
import {StatusLegend} from 'components/organisms/Map/_statusLegend';
import {SimpleMarker} from 'components/organisms/Map/_simpleMarker';
import {mapSettings} from 'components/organisms/Map/map-settings';
import {GOOGLE_MAPS_API_KEY} from 'components/organisms/Map/settings';
import { A } from '@atoms/LinksAndButtons/A';
import { getFormattedMessage } from 'features/services/utils/language-helper';

const PIN_ZOOM_DISTANCE = 12;

interface Props {
    residences?: SearchResult[];
    customOffsetDesktop?: number;
    customOffsetMobile?: number;
    marker?: { 
        lat: number, 
        lng: number, 
        stageStatus?: StageStatus, 
        residenceType?: ResidenceType 
    }
}

export interface GeoJson {
    type: string,
    properties: {
        cluster: boolean,
        category: ResidenceType,
        stageId: string,
        residence: SearchResult,
        objects: SearchResult[],
    },
    geometry: {
        type: string,
        coordinates: number[],
    }
}

export const Map: React.FunctionComponent<Props> = ({ residences , customOffsetDesktop, customOffsetMobile, marker}) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Google Maps ref with dynamic API
    const mapRef = useRef<any>(null);
    const beforeMapRef = useRef<HTMLAnchorElement>(null);
    const afterMapRef = useRef<HTMLAnchorElement>(null);
    const [mapReady, setMapReady] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Google Maps bounds object
    const [mapBounds, setMapBounds] = useState<any>(null);
    const [mapZoom, setMapZoom] = useState<number>(16);
    const [points, setPoints] = useState<GeoJson[]>([]);
    const [activeMarker, setActiveMarker] = useState<GeoJson | null>(null);
    const [windowPosition, setWindowPosition] = useState<{ lng: number, lat: number } | null>(null);

    const { searchResult } = useSearchData();
    const isMobile = useDeviceStore(selectIsMobile);

    const defaultProps = {
        lat: 59.34,
        lng: 18.15
    };

    useEffect(() => {
        if(!mapReady) return;

        if(marker) {
            const newBounds = new google.maps.LatLngBounds();
            newBounds.extend({lat: marker.lat, lng: marker.lng});

            mapRef.current.fitBounds(newBounds);
            mapRef.current.panToBounds(newBounds);

            return;
        }

        if(residences && residences.length > 0) {
            setupResidenceMapData();
            setCenter(residences);

            return;
        }

        if(searchResult.stage.length > 0) {
            setupMapData();
            setCenter(searchResult[SearchTypes.STAGE]);
        }
    }, [mapReady, searchResult]);

    useEffect(() => {
        if(mapZoom) {
            setWindowPosition(windowPosition);
        }
    }, [mapZoom]);

    const setupMapData = () => {
        const validStages = searchResult[SearchTypes.STAGE].filter(filterValidStages);
        const pointsFromStages = validStages.map((stage) => {

            return {
                type: "Feature",
                properties: {
                    cluster: false,
                    category: stage.residenceType,
                    stageId: stage.id,
                    residence: stage,
                    objects: searchResult[SearchTypes.OBJECTS].filter((obj) => obj !== null && obj.stage === stage.id),
                },
                geometry: {
                    type: "Point",
                    // @ts-expect-error
                    coordinates: [parseFloat(stage._geo.lng), parseFloat(stage._geo.lat)],
                }
            }
        });

        setPoints(pointsFromStages);
    };

    const setupResidenceMapData = () => {
        if(!residences) return;

        const validStages = residences.filter(filterValidStages);
        const pointsFromStages = validStages.map((stage) => {
            return {
                type: "Feature",
                properties: {
                    cluster: false,
                    category: stage.residenceType,
                    stageId: stage.id,
                    residence: stage,
                    objects: searchResult[SearchTypes.OBJECTS].filter((obj) => obj !== null && obj.stage === stage.id),
                },
                geometry: {
                    type: "Point",
                    // @ts-expect-error
                    coordinates: [parseFloat(stage._geo.lng), parseFloat(stage._geo.lat)],
                }
            }
        });

        setPoints(pointsFromStages);
    };

    const filterValidStages = (stage: SearchResult) => {
        if(stage._geo?.lng && stage._geo?.lat) {

            if(stage._geo?.lng < 100 && stage._geo?.lat < 200) return true;
        }

        return false;
    };

    const onMapChange = ({ bounds, zoom }) => {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        setMapBounds( [sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
        setMapZoom(zoom);
    };

    const onGoogleApiLoaded = ({ map }) => {
        mapRef.current = map;

        google.maps.event.addListener(map, 'zoom_changed', onZoomChange);

        map.addListener("click", () => {
            setActiveMarker(null);
        });

        setMapReady(true);
    };

    const setCenter = (residences: SearchResult[]) => {
        if(mapRef.current === null) return;
        if(residences && residences.length > 0) {
            const newBounds = new google.maps.LatLngBounds();

            residences.filter(filterValidStages).map((marker) => {
                if(!marker._geo) return;

                newBounds.extend({lat: marker._geo.lat, lng: marker._geo.lng});
            });

            mapRef.current.fitBounds(newBounds);
            mapRef.current.panToBounds(newBounds);
        }
    };

    const onZoomChange = () => {
        //setActiveMarker(null);
    };

    const onMarkerClick = (cluster : GeoJson) => () => {
        if(mapRef.current === null) return;

        const clusterLatLng = new google.maps.LatLng(cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]);

        if(clusterLatLng !== null) {
            setActiveMarker(cluster);

            if(isMobile) {
                const mapPos = offsetCenter(clusterLatLng, 0, customOffsetMobile ? customOffsetMobile : 210);
                mapRef.current.panTo(mapPos);
            } else {
                const mapPos = offsetCenter(clusterLatLng, 0, customOffsetDesktop ? customOffsetDesktop : 110);
                mapRef.current.panTo(mapPos);
            }
        }
    };

    const onDotClick = (cluster : GeoJson) => () => {
        if(mapRef.current === null) return;
        mapRef.current.setZoom(PIN_ZOOM_DISTANCE);

        const clusterLatLng = new google.maps.LatLng(cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]);

        mapRef.current.panTo(clusterLatLng);
    };

    const onOutSideClick = () => {
        setActiveMarker(null);
    };

    const offsetCenter = (latlng: google.maps.LatLng, offsetx: number, offsety: number, zoom?: number) => {
        if(mapRef.current === null) return;

        const scale = Math.pow(2, zoom ? zoom : mapRef.current.getZoom());
        const worldCoordinateCenter = mapRef.current.getProjection().fromLatLngToPoint(latlng);
        const pixelOffset = new google.maps.Point((offsetx/scale) || 0,(offsety/scale) ||0);

        if(worldCoordinateCenter === null) return new google.maps.LatLng(0,0);

        const worldCoordinateNewCenter = new google.maps.Point(
            worldCoordinateCenter.x - pixelOffset.x,
            worldCoordinateCenter.y + pixelOffset.y
        );

        return mapRef.current.getProjection().fromPointToLatLng(worldCoordinateNewCenter);
    };

    const { clusters, supercluster } = useSupercluster({
        points: points,
        bounds: mapBounds,
        zoom: mapZoom,
        options: {
            radius: 60,
            maxZoom: 14,
            minZoom: PIN_ZOOM_DISTANCE,
        }
    });

    const skipMap = (direction: 'up' | 'down') => {
        if(direction === 'down') {
            afterMapRef.current?.focus();
        } else if (direction === 'up') {
            beforeMapRef.current?.focus();
        }
    };

    return (
        <div className="jm-google-maps" id="jm-google-maps">
            <A 
                href="#" 
                onClick={(e) => {
                    e.preventDefault(); 
                    skipMap('down');
                }}
                ref={beforeMapRef} 
                className='visually-hidden' 
                title={getFormattedMessage('jm.components.Map.skipDown')} 
            >
                <FormattedMessage id='jm.components.Map.skipDown'/>
            </A>
            {activeMarker !== null && (
                <CardHolder
                    jsonData={activeMarker}
                    onCloseClick={onOutSideClick}
                />
            )}

            { mapReady && !residences && !marker && mapZoom >= PIN_ZOOM_DISTANCE &&
                <StatusLegend />
            }

            <GoogleMap
                apiKey={GOOGLE_MAPS_API_KEY}
                defaultZoom={11}
                defaultCenter={{ lat: defaultProps.lat, lng: defaultProps.lng }}
                onGoogleApiLoaded={onGoogleApiLoaded}
                onChange={onMapChange}
                options={mapSettings}
                yesIWantToUseGoogleMapApiInternals
            >
                {marker &&
                    <SimpleMarker
                        lat={marker.lat}
                        lng={marker.lng}
                        residenceType={marker.residenceType}
                        stageStatus={marker.stageStatus}
                    />
                }

                {!marker && clusters.map(cluster => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const {
                        cluster: isCluster,
                        point_count: pointCount
                    } = cluster.properties;

                    if(mapZoom < PIN_ZOOM_DISTANCE) return (
                        <MarkerDot
                            lat={latitude}
                            lng={longitude}
                            onClick={onDotClick(cluster)}
                            residenceType={cluster.properties.category}
                            key={`cluster-${cluster.id}-${cluster.properties.stageId}`}
                            stage={cluster.properties.residence}
                            isCluster={isCluster}
                        />
                    );

                    if (isCluster) {
                        return (
                            <Cluster
                                lat={latitude}
                                lng={longitude}
                                count={pointCount}
                                key={`cluster-${cluster.id}`}
                                style={{ zIndex: 1 }}
                                onClick={() => {

                                    if(mapRef.current === null) return;

                                    const expansionZoom = Math.min(
                                        supercluster.getClusterExpansionZoom(cluster.id),
                                        16
                                    );
                                    mapRef.current.setZoom(expansionZoom);
                                    mapRef.current.panTo({ lat: latitude, lng: longitude });
                                    setActiveMarker(null);
                                }}
                            />
                        );
                    }

                    return (
                        <Marker
                            lat={latitude}
                            lng={longitude}
                            key={`cluster-${cluster.properties.stageId}`}
                            onClick={onMarkerClick(cluster)}
                            residenceType={cluster.properties.category}
                            stage={cluster.properties.residence}
                            objects={cluster.properties.objects}
                            active={activeMarker === cluster}
                        >
                        </Marker>
                    );
                })}
            </GoogleMap>
            <A 
                href="#" 
                onClick={(e) => {
                    e.preventDefault(); 
                    skipMap('up');
                }}
                ref={afterMapRef}
                className='visually-hidden' 
                title={getFormattedMessage('jm.components.Map.skipUp')} 
            >
                <FormattedMessage id='jm.components.Map.skipUp'/>
            </A>
        </div>
    );
};
